import Toast from 'react-native-toast-message'
import findAssociativeChanges from '../utils/findAssociativeChanges';
import { createNotification } from './notifications';
import { getCurrentOrgID } from '../data/organization';
import ErrorHandler from '../utils/ErrorHandler';
import { isTransientError } from '@/utils/requestQueue';
import { supabase } from '../data/client';
import { insertActivity } from './activity';
import { buildUpdateDetails, joinDetails } from '../utils/buildActivityDetails';
import { PEOPLE_SELECT } from '../utils/peopleSelect';
import { isAutomationDueType, isFollowUpDueType } from '@/utils/constants/taskTypes';

// Resolves the number of assignees needed for a task instance.
// assigneesNeeded lives on VolunteerTeamTasks (per task within a team).
const getAssigneesNeeded = (taskInstance) => {
    if(!taskInstance) return 1;
    const vtt = taskInstance.VolunteerTeam?.VolunteerTeamTasks?.find(v => v.taskID === taskInstance.taskID);
    return vtt?.assigneesNeeded || taskInstance.VolunteerTeam?.assigneesNeeded || 1;
}

// Returns the deduped titles of other task instances within the same event
// instance that the given person is also assigned to. Used to surface *which*
// tasks a scheduling conflict is with (task names are within RLS perms even
// when the assigner can't see the other volunteer teams).
const getConflictingTaskTitles = (taskInstance, personID) => {
    if(!taskInstance?.EventInstance?.TaskInstances || !personID) return [];
    const titles = new Set();
    taskInstance.EventInstance.TaskInstances.forEach(t => {
        if(!t || t.id === taskInstance.id) return;
        if(t.Assignees?.some(a => a?.id === personID) && t.Task?.title){
            titles.add(t.Task.title);
        }
    });
    return [...titles];
}

const getTasks = async (type = null) => {
    const orgID = await getCurrentOrgID();
    
    let query = supabase.from('Tasks').select('*, EventTasks(*, Event:Events(*)), VolunteerTeamTasks(*, VolunteerTeam:VolunteerTeams(id, name))').eq('organizationID', orgID).order('title', {ascending: true});

    if(type){
        // Accepts a single dueType or an array (dual-read PATHS+AUTOMATIONS).
        query = Array.isArray(type) ? query.in('dueType', type) : query.eq('dueType', type)
    }

    const {error, data} = await query;

    if(error){
        if(!isTransientError(error)){
            ErrorHandler(Error(error.message), {organizationID: orgID, error}, `Something went wrong getting tasks`)
        }
        return [];
    }

    return data.map(task => ({...task, Events: task.EventTasks?.map(et => et.Event).filter(Boolean) || []}));
}

const getTask = async (taskID) => {
    const orgID = await getCurrentOrgID(); 

    const {error, data} = await supabase.from('Tasks').select(`*, 
        EventTasks(*, Event:Events(*)),
        VolunteerTeamTasks(*, VolunteerTeam:VolunteerTeams(id, name)),
        AssignmentMembers:TaskAssignmentMembers(*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)),
        TaskInstances(*, Assignees:TaskInstancePeople(*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)), Task:Tasks(*), VolunteerTeam:VolunteerTeams(id, name, VolunteerTeamTasks(taskID, assigneesNeeded), VolunteerTeamAssigners(personID)), EventInstance:EventInstances(*, Event:Events(name, Calendar:Calendars(color)), TaskInstances(*, Assignees:People!TaskInstancePeople(${PEOPLE_SELECT}))))
    `).eq('organizationID', orgID).eq('id', taskID).eq('TaskInstances.completed', false).eq('TaskInstances.isDeleted', false).maybeSingle();
    
    if(error){
        if(!isTransientError(error)){
            ErrorHandler(Error(error.message), {organizationID: orgID, taskID, error}, `Something went wrong getting that task`)
        }
        throw Error()
    }

    data.Events = data.EventTasks?.map(et => et.Event).filter(Boolean) || [];

    data.TaskInstances?.forEach(a => {
        a.Assignees = a.Assignees?.map(a => ({...a, ...a.Person}))

        a.EventInstance?.TaskInstances?.forEach((taskInstance, taskInstanceIndex) => {
            const currentAssigneeIDs = a.Assignees?.map(a => a.personID);

            if(taskInstance.id == a.id){
                return;
            }

            if(taskInstance.Assignees?.length > 0 && currentAssigneeIDs.length > 0){
                for(let i=0; i<taskInstance.Assignees?.length; i++){
                    const assignee = taskInstance.Assignees[i];

                    if(currentAssigneeIDs.includes(assignee?.id)){
                        const assigneeIndex = a.Assignees.findIndex(ti_assignee => ti_assignee?.id == assignee?.id)

                        if(!a.Assignees[assigneeIndex].conflicts){
                            a.Assignees[assigneeIndex].conflicts = [];
                        }

                        a.Assignees[assigneeIndex].conflicts.push({
                            Task: taskInstance.Task,
                            taskInstanceIndex: taskInstanceIndex,
                            assigneeIndex: i
                        })
                    }
                }
            }
        })
    })

    return data;
}


const deleteTask = async ({taskID}) => {
    const {data: existing} = await supabase.from('Tasks').select('title').eq('id', taskID).maybeSingle();

    const {error} = await supabase.from('Tasks').delete().eq('id', taskID);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deleteTask", taskID, error}, `Something went wrong deleting that task`)
        throw Error()
    }

    Toast.show({ type: 'success', text2: `Task has been deleted successfully!`});

    insertActivity({ subjectType: 'Task', subjectID: taskID, subjectName: existing?.title, verb: 'delete' }).catch(() => {});
}


const getTaskInstance = async (taskInstanceID) => {
    const {error, data} = await supabase.from('TaskInstances').select(`*,
        Task:Tasks(*),
        VolunteerTeam:VolunteerTeams(id, name, VolunteerTeamTasks(taskID, assigneesNeeded), VolunteerTeamAssigners(personID)),
        EventInstance:EventInstances(*, TaskInstances(*), Event:Events(name, Calendar:Calendars(color))),
        PathEnrollmentSteps:PathEnrollmentSteps(id, pathStepID, PathStep:PathSteps(actionType), PathEnrollment:PathEnrollments(id, pathID, Person:People!PathEnrollments_personID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)))
    `).eq('id', taskInstanceID).maybeSingle();
    const {error: assigneesError, data: assignees} = await supabase.from('TaskInstancePeople').select('*, Assignee:People(id,firstName,lastName,avatarUpdatedAt,avatarStorageKey)').eq('taskInstanceID', taskInstanceID);

    if(error || assigneesError){
        const err = error || assigneesError;
        if(!isTransientError(err)){
            ErrorHandler(Error(error.message || assigneesError.message), {taskInstanceID, error, assigneesError}, `Something went wrong getting this task instance`)
        }
        return [];
    }

    data.Assignees = assignees.map(a => ({...a.Assignee, ...a}))
    return data;
}


const getTaskInstancesByTask = async (taskID, showCompleted = false) => {
    let query = supabase.from('TaskInstances')
    .select(`*, Assignees:People!TaskInstancePeople(${PEOPLE_SELECT}), Task:Tasks(*), VolunteerTeam:VolunteerTeams(id, name, VolunteerTeamTasks(taskID, assigneesNeeded), VolunteerTeamAssigners(personID)), EventInstance:EventInstances(*, Event:Events(*), TaskInstances(*, Assignees:People!TaskInstancePeople(${PEOPLE_SELECT})))`)
    .eq('taskID', taskID).eq('isDeleted', false);

    if(!showCompleted){
        query = query.eq('completed', false)
    }

    const {error, data} = await query;

    if(error){
        if(!isTransientError(error)){
            ErrorHandler(Error(error.message), {taskID, error}, `Something went wrong getting task instances`)
        }
        return [];
    }

    data.forEach(a => {
        a.EventInstance?.TaskInstances?.forEach((taskInstance, taskInstanceIndex) => {
            const currentAssigneeIDs = a.Assignees?.map(a => a.personID);

            if(taskInstance.id == a.id){
                return;
            }

            if(taskInstance.Assignees?.length > 0 && currentAssigneeIDs.length > 0){
                for(let i=0; i<taskInstance.Assignees?.length; i++){
                    const assignee = taskInstance.Assignees[i];

                    if(currentAssigneeIDs.includes(assignee?.id)){
                        const assigneeIndex = a.Assignees.findIndex(ti_assignee => ti_assignee?.id == assignee?.id)

                        if(!a.Assignees[assigneeIndex].conflicts){
                            a.Assignees[assigneeIndex].conflicts = [];
                        }

                        a.Assignees[assigneeIndex].conflicts.push({
                            Task: taskInstance.Task,
                            taskInstanceIndex: taskInstanceIndex,
                            assigneeIndex: i
                        })
                    }
                }
            }
        })
    })

    return data;
}


const getPersonTaskHistory = async (personID) => {
    if(!personID) return [];

    const {error, data} = await supabase.from('TaskInstances')
        .select(`*,
            PersonAssignment:TaskInstancePeople!inner(personID),
            Assignees:TaskInstancePeople(*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)),
            Task:Tasks(*),
            VolunteerTeam:VolunteerTeams(id, name, VolunteerTeamTasks(taskID, assigneesNeeded)),
            EventInstance:EventInstances(*, Event:Events(name), TaskInstances(*, Assignees:TaskInstancePeople(*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey))))
        `)
        .eq('PersonAssignment.personID', personID)
        .eq('isDeleted', false)
        .order('dueDate', {ascending: false});

    if(error){
        if(!isTransientError(error)){
            ErrorHandler(Error(error.message), {personID, error}, `Something went wrong getting task history`)
        }
        throw error;
    }

    const now = new Date();
    return data.filter(taskInstance => {
        if(taskInstance.completed) return true;
        // Cancelled work is never live work — it belongs in history, but
        // must never be presented as completed.
        if(taskInstance.cancelledAt) return true;
        // Automation/Follow Up tasks are history only when completed or
        // deleted — a past dueDate is a reassignment timeout, not an
        // assumed completion (spec §10.7).
        if(isAutomationDueType(taskInstance.Task?.dueType) || isFollowUpDueType(taskInstance.Task?.dueType)) return false;
        const dueDateValue = taskInstance.dueDate || taskInstance.EventInstance?.startTime;
        return Boolean(taskInstance.Task?.assumeCompleted && dueDateValue && new Date(dueDateValue) < now);
    }).map(taskInstance => ({
        ...taskInstance,
        Assignees: taskInstance.Assignees?.map(assignee => ({...assignee, ...assignee.Person})) || [],
    }));
}


const upcomingTaskInstancesForPerson = async (personID, horizonDays = 7) => {
    const orgID = await getCurrentOrgID(); 

    const {error, data} = await supabase.rpc('get_upcoming_task_instances', {
        person_id: personID,
        horizon_days: horizonDays
    });

    if(error){
        if(!isTransientError(error)){
            ErrorHandler(Error(error.message), {organizationID: orgID, personID, horizonDays, error}, `Something went wrong getting tasks`)
        }
        return [];
    }

    return Array.isArray(data) ? data : [];
}


const upcomingTaskInstancesAsAssigner = async (personID, horizonDays = 30) => {
    const orgID = await getCurrentOrgID();

    const {error, data} = await supabase.rpc('get_upcoming_task_assignments', {
        person_id: personID,
        horizon_days: horizonDays
    });

    if(error){
        if(!isTransientError(error)){
            ErrorHandler(Error(error.message), {organizationID: orgID, personID, horizonDays, error}, `Something went wrong getting task assignments`)
        }
        return [];
    }

    return Array.isArray(data) ? data.map(taskInstance => ({
        ...taskInstance,
        VolunteerTeam: taskInstance.VolunteerTeam ? {
            ...taskInstance.VolunteerTeam,
            VolunteerTeamAssigners: [{personID}]
        } : null
    })) : [];
}


// Team-scoped variant of the get_upcoming_task_assignments RPC for viewers
// who are not assigners: same filters and shape, but keyed by team instead of
// the caller's assigner status. VolunteerTeamAssigners comes from the DB (not
// injected) so TaskCard still gates assigner-only controls correctly.
const upcomingTaskInstancesForTeam = async (teamID, horizonDays = 30) => {
    if(!teamID) return [];
    const orgID = await getCurrentOrgID();
    const horizon = new Date(Date.now() + Math.max(horizonDays ?? 30, 0) * 24 * 60 * 60 * 1000);

    const {error, data} = await supabase.from('TaskInstances')
        .select(`*,
            Assignees:TaskInstancePeople(*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)),
            Task:Tasks(*),
            VolunteerTeam:VolunteerTeams(id, name, VolunteerTeamTasks(taskID, assigneesNeeded), VolunteerTeamAssigners(personID)),
            EventInstance:EventInstances(*, Event:Events(name, Calendar:Calendars(color)), TaskInstances(*, Task:Tasks(title), Assignees:People!TaskInstancePeople(id)))`)
        .eq('organizationID', orgID)
        .eq('volunteerTeamID', teamID)
        .eq('isDeleted', false)
        .eq('completed', false)
        .is('cancelledAt', null)
        .or(`dueDate.lt.${horizon.toISOString()},dueDate.is.null`);

    if(error){
        if(!isTransientError(error)){
            ErrorHandler(Error(error.message), {organizationID: orgID, teamID, horizonDays, error}, `Something went wrong getting team task instances`)
        }
        return [];
    }

    // PostgREST can't reach ei.startTime in the top-level filter, so finish the
    // RPC's horizon predicate client-side: dueDate < horizon, else (when
    // dueDate is null) EventInstance.startTime < horizon.
    return (data || [])
        .filter(ti => ti.dueDate ? new Date(ti.dueDate) < horizon : !!(ti.EventInstance?.startTime && new Date(ti.EventInstance.startTime) < horizon))
        .map(ti => ({
            ...ti,
            Assignees: ti.Assignees?.map(a => ({...a, ...a.Person})) || [],
            EventInstance: ti.EventInstance ? {
                ...ti.EventInstance,
                TaskInstances: (ti.EventInstance.TaskInstances || []).filter(sibling => !sibling.isDeleted),
            } : ti.EventInstance,
        }));
}


const getIndividualTaskInstances = async () => {
    const orgID = await getCurrentOrgID();

    const { error, data } = await supabase.from('TaskInstances')
        .select('*, Task:Tasks!inner(*), Assignees:TaskInstancePeople(*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey))')
        .eq('organizationID', orgID)
        .eq('isDeleted', false)
        .eq('Task.dueType', 'INDIVIDUAL')
        .order('dueDate', { ascending: false });

    if(error){
        if(!isTransientError(error)){
            ErrorHandler(Error(error.message), {organizationID: orgID, error}, `Something went wrong getting individual task instances`)
        }
        return [];
    }

    return data.map(ti => ({
        ...ti,
        Assignees: ti.Assignees?.map(a => ({...a, ...a.Person})) || []
    }));
}


const getTaskInstancesByEventInstance = async (eventInstanceID) => {
    if(!eventInstanceID){
        return [];
    }

    const {data} = await supabase.from('TaskInstances')
    .select(`*, Assignees:People!TaskInstancePeople(${PEOPLE_SELECT}), Task:Tasks(*, EventInstance:EventInstances(*, TaskInstances(*, Assignees(*)))), VolunteerTeam:VolunteerTeams(id, name, VolunteerTeamTasks(taskID, assigneesNeeded), VolunteerTeamAssigners(personID))`).eq('eventInstanceID', eventInstanceID).eq('isDeleted', false);

    const assignees = {}

    //List out all the assignees that are used on this Event Instance and what the task is
    data.forEach((a, index) => {
        a.Assignees = a.Assignees?.items

        if(a.Assignees?.length > 0){
            for(let i=0; i<a.Assignees?.length; i++){
                const assignee = a.Assignees[i];

                if(!assignees[assignee?.id]){
                    assignees[assignee?.id] = [];
                }

                assignees[assignee?.id].push({
                    Task: a.Task,
                    taskInstanceIndex: index,
                    assigneeIndex: i
                })
            }
        }
    })

    const assigneeIDs = Object.keys(assignees);

    //For each assignee, if they are assigned to more than one task, add the other tasks to the conflicts array
    for(let i=0; i<assigneeIDs?.length; i++){
        const id = assigneeIDs[i];

        if(assignees[id].length > 1){
            assignees[id].forEach(a => {
                const assigneesMinusThisOne = assignees[id].filter(b => b.Task?.id != a.Task?.id)
                data[a.taskInstanceIndex].Assignees[a.assigneeIndex].conflicts = assigneesMinusThisOne;
            })
        }
    }

    return data;
}


const updateAssigneeResponse = async ({personID, taskInstanceID, newResponse, reason, loggedInUserID, forwardToID}) => {
    // Guard against undefined personID/taskInstanceID — callers (e.g. blackout period
    // decline-all in events.js) may pass undefined when the Assignee join didn't populate.
    // Supabase serializes undefined as the string "undefined" → invalid uuid error.
    // See CHURCH-APP-133.
    if(!personID || !taskInstanceID){
        console.warn('[updateAssigneeResponse] Missing personID or taskInstanceID, skipping');
        return null;
    }

    const {error, count} = await supabase.from('TaskInstancePeople')
        .update({assignmentResponse: newResponse, reason: reason}, {count: 'exact'})
        .eq('personID', personID)
        .eq('taskInstanceID', taskInstanceID);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateAssigneeResponse", input: {personID, taskInstanceID, newResponse, reason}}, `Something went wrong updating that task`)
        throw error;
    }
    if(!count){
        Toast.show({type: 'error', text2: 'This assignment is no longer available. Please refresh your tasks.'});
        return null;
    }

    const {data: taskInstancePerson, error: readError} = await supabase.from('TaskInstancePeople')
        .select('*, Assignee:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey), TaskInstance:TaskInstances(*, EventInstance:EventInstances(startTime, Event:Events(id, name)), VolunteerTeam:VolunteerTeams(id, name), Assignees:TaskInstancePeople(assignmentResponse, position, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)), Task:Tasks(*))')
        .eq('personID', personID)
        .eq('taskInstanceID', taskInstanceID)
        .maybeSingle();

    if(readError){
        ErrorHandler(Error(readError.message), {mutation: "updateAssigneeResponse", input: {personID, taskInstanceID, newResponse, reason}}, `Something went wrong fetching that task`);
    }
    if(!taskInstancePerson) return {personID, taskInstanceID};

    const accepted = newResponse == "ACCEPTED" ? true : false;

    let foundBackup;
    const notificationsToSend = [];

    if(!accepted && !forwardToID){
        taskInstancePerson.TaskInstance?.Assignees?.sort((a,b) => a.position - b.position).forEach(a => {
            if(a.position > taskInstancePerson.position && a.assignmentResponse != "DECLINED"){
                foundBackup = a.Person
            }
        })

        if(foundBackup){
            const taskInstance = taskInstancePerson.TaskInstance
            const startTime = taskInstance.EventInstance?.startTime
            const teamSuffix = taskInstance.VolunteerTeam?.name ? ` (${taskInstance.VolunteerTeam.name})` : ''
            const message = startTime
                ? `${taskInstance.Task?.title}${teamSuffix} has been assigned to you for ${taskInstance.EventInstance?.Event?.name ?? 'an event'} on ${new Date(startTime).toLocaleString("en-US", {month:"2-digit", day:"2-digit", year:"numeric", hour:"numeric", minute:"2-digit"})}`
                : taskInstance.dueDate
                    ? `${taskInstance.Task?.title} has been assigned to you and is due on ${new Date(taskInstance.dueDate).toLocaleString("en-US", {month:"2-digit", day:"2-digit", year:"numeric", hour:"numeric", minute:"2-digit"})}`
                    : `${taskInstance.Task?.title}${teamSuffix} has been assigned to you`
    
            const notification = {
                title: "📋 You've been assigned a new task",
                path: `tasks/instances/${taskInstance.id}`,
                targetPersonID: foundBackup.id,
                type: "TASK_ASSIGNED",
                message: message,
                parameters: {taskInstanceID: taskInstance.id},
            }
    
            notificationsToSend.push(notification)
        }
    }

    // Notify volunteer team assigners when the assignee ladder is resolved:
    //   - accepted (ladder resolved), OR
    //   - declined with no backup and no forward (ladder exhausted, assigner must act)
    // Skip when chain continues to a backup or forwarded person.
    if(accepted || (!accepted && !foundBackup && !forwardToID)){
        const taskInstance = taskInstancePerson.TaskInstance
        const volunteerTeamID = taskInstance?.volunteerTeamID ?? taskInstance?.VolunteerTeam?.id

        if(volunteerTeamID){
            const responderID = taskInstancePerson.Assignee?.id ?? personID
            const responderName = `${taskInstancePerson.Assignee?.firstName ?? ''} ${taskInstancePerson.Assignee?.lastName ?? ''}`.trim() || 'Someone'
            const responseVerb = accepted ? 'accepted' : 'declined'
            const startTime = taskInstance.EventInstance?.startTime
            const teamSuffix = taskInstance.VolunteerTeam?.name ? ` (${taskInstance.VolunteerTeam.name})` : ''
            const message = startTime
                ? `${responderName} has ${responseVerb} ${taskInstance.Task?.title}${teamSuffix} for ${taskInstance.EventInstance?.Event?.name ?? 'an event'} on ${new Date(startTime).toLocaleString("en-US", {month:"2-digit", day:"2-digit", year:"numeric", hour:"numeric", minute:"2-digit"})}`
                : taskInstance.dueDate
                    ? `${responderName} has ${responseVerb} ${taskInstance.Task?.title}, due ${new Date(taskInstance.dueDate).toLocaleString("en-US", {month:"2-digit", day:"2-digit", year:"numeric", hour:"numeric", minute:"2-digit"})}`
                    : `${responderName} has ${responseVerb} ${taskInstance.Task?.title}`

            const {data: assigners} = await supabase.from('VolunteerTeamAssigners')
                .select('personID')
                .eq('volunteerTeamID', volunteerTeamID)

            assigners?.forEach(({personID: assignerPersonID}) => {
                if(assignerPersonID == responderID) return
                notificationsToSend.push({
                    title: `📋 ${responderName} ${responseVerb} a task`,
                    path: `tasks/instances/${taskInstance.id}`,
                    targetPersonID: assignerPersonID,
                    type: "TASK_RESPONSE",
                    message: message,
                    parameters: {taskInstanceID: taskInstance.id, personID: responderID, response: newResponse},
                })
            })
        }
    }

    if(forwardToID && taskInstancePerson.TaskInstance?.Assignees?.map(a => a.Person?.id).indexOf(forwardToID) == -1){
        updateAssignees({
            taskInstanceID: taskInstanceID, 
            newAssignees: [{id: forwardToID, position: taskInstancePerson.TaskInstance?.Assignees?.length}], 
            taskInstance: {...taskInstancePerson.TaskInstance, Assignees: taskInstancePerson.TaskInstance?.Assignees?.map(a => ({...a, ...a.Person}))}, 
            currentUser: loggedInUserID, 
            assignmentResponse: "NO_RESPONSE"
        })
    }

    if(notificationsToSend.length > 0){
        await createNotification(notificationsToSend);
    }

    return taskInstancePerson;
}


const updateTaskCompletionStatus = async ({id, completed}) => {
    const {error, data} = await supabase.from('TaskInstances').update({completed: completed}).eq('id', id).select('id');

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateTaskCompletionStatus", input: {id, completed}}, `Something went wrong updating that task`)
        throw error;
    }

    // RLS can silently block the update — it "succeeds" but matches 0 rows.
    if(!data?.length){
        ErrorHandler(Error('Task instance update matched no rows'), {mutation: "updateTaskCompletionStatus", input: {id, completed}}, `Something went wrong updating that task`)
        throw Error('Task instance update matched no rows');
    }
}


const updateAssignees = async ({taskInstanceID, existingAssignees = [], newAssignees = [], taskInstance, currentUser = null, assignmentResponse = "NO_RESPONSE", notifyAll = false}) => {
    const orgID = await getCurrentOrgID();
    const returnValue = [];

    // Resolve the team this instance belongs to so assignments are scoped to the correct
    // team (a task can exist on multiple teams, each with its own TaskInstance).
    let volunteerTeamID = taskInstance?.volunteerTeamID ?? null;
    let teamName = taskInstance?.VolunteerTeam?.name ?? null;
    if(volunteerTeamID == null || teamName == null){
        const {data: tiRow} = await supabase.from('TaskInstances').select('volunteerTeamID, VolunteerTeam:VolunteerTeams(name)').eq('id', taskInstanceID).maybeSingle();
        volunteerTeamID = volunteerTeamID ?? tiRow?.volunteerTeamID ?? null;
        teamName = teamName ?? tiRow?.VolunteerTeam?.name ?? null;
    }

    const { itemsToAdd, itemsToDelete, unchangedItems } = findAssociativeChanges(newAssignees, existingAssignees);
    
    if(itemsToAdd.length > 0){
        const {error, data: assignees} = await supabase.from('TaskInstancePeople').insert(itemsToAdd?.map((a, i) => ({taskInstanceID: taskInstanceID, organizationID: orgID, personID: a.id, assignmentResponse: assignmentResponse, position: a.position == null ? i : a.position, volunteerTeamID: volunteerTeamID}))).select('*');

        returnValue.push([...assignees])

        if(error){
            ErrorHandler(Error(error.message), {mutation: "createTaskInstancePerson", itemsToAdd, error}, `Something went wrong adding new assignees`)
            return [];
        }

        const nextPositionInLine = Math.min(...newAssignees.filter(a => a.assignmentResponse != 'DECLINED').map(a => a.position ? a.position : 0))
        const notificationsToSend = [];

        assignees.forEach(newAssignee => {
            if(newAssignee.personID == currentUser){
                //Don't send a notification if the current user assigned to themself
                return newAssignee;
            }
            else if(!notifyAll && (newAssignee.position < nextPositionInLine || newAssignee.position >= getAssigneesNeeded(taskInstance))){
                //Don't send a notification if the new assignee has a lower 'position' than someone else who has not declined the task
                //e.g. only position 0 should get a notification, if 0 declines, 1 gets it, and so on
                return newAssignee;
            }
    
            const startTime = taskInstance.EventInstance?.startTime
            const teamSuffix = teamName ? ` (${teamName})` : ''
            const message = startTime
                ? `${taskInstance.Task?.title}${teamSuffix} has been assigned to you for ${taskInstance.EventInstance?.Event?.name ?? 'an event'} on ${new Date(startTime).toLocaleString("en-US", {month:"2-digit", day:"2-digit", year:"numeric", hour:"numeric", minute:"2-digit"})}`
                : taskInstance.dueDate
                    ? `${taskInstance.Task?.title} has been assigned to you and is due on ${new Date(taskInstance.dueDate).toLocaleString("en-US", {month:"2-digit", day:"2-digit", year:"numeric", hour:"numeric", minute:"2-digit"})}`
                    : `${taskInstance.Task?.title}${teamSuffix} has been assigned to you`
    
            const notification = {
                title: "You've been assigned a new task",
                path: `tasks/instances/${taskInstance.id}`,
                type: "TASK_ASSIGNED",
                targetPersonID: newAssignee.personID,
                message: message,
                parameters: {taskInstanceID: taskInstance.id},
            }
    
            notificationsToSend.push(notification)
        });
        
        if(notificationsToSend.length > 0){
            createNotification(notificationsToSend);
        }
    }
    
    if(itemsToDelete.length > 0){
        const {error: deleteError} = await supabase.from('TaskInstancePeople').delete().eq('taskInstanceID', taskInstanceID).in('personID', itemsToDelete?.map(a => a.id))

        if(deleteError){
            ErrorHandler(Error(deleteError.message), {mutation: "deleteTaskInstancePerson", itemsToDelete, deleteError}, `Something went wrong removing assignees`)
            return [];
        }
    }

    unchangedItems.forEach(async assignee => {
        const {error} = await supabase.from('TaskInstancePeople').update({position: assignee.position}).eq('personID', assignee.id).eq('taskInstanceID', taskInstanceID);

        if(error){
            ErrorHandler(Error(error.message), {mutation: "updateTaskInstancePersonPosition", input: {assignee, taskInstanceID}, error})
        }
        
    })

    const unchangedAssignees = existingAssignees.filter(a => unchangedItems?.map(u => u.id).indexOf(a.id) > -1)

    // Log assignment changes (fire and forget)
    const taskName = taskInstance?.Task?.title || 'Task';
    const addedNames = itemsToAdd.map(a => `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim()).filter(Boolean);
    const removedNames = itemsToDelete.map(a => `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim()).filter(Boolean);

    if(addedNames.length > 0){
        insertActivity({
            subjectType: 'Task',
            subjectID: taskInstanceID,
            subjectName: taskName,
            verb: 'assign',
            details: addedNames.map(n => `- Assigned ${n}`).join('\n'),
        }).catch(() => {});
    }

    if(removedNames.length > 0){
        insertActivity({
            subjectType: 'Task',
            subjectID: taskInstanceID,
            subjectName: taskName,
            verb: 'unassign',
            details: removedNames.map(n => `- Unassigned ${n}`).join('\n'),
        }).catch(() => {});
    }

    return [...returnValue, ...unchangedAssignees];
}


const saveTask = ({details, originalValues, currentUser = null}) => {
    // Follow Up care tasks are created and managed only through the
    // follow_up_*_task RPCs (spec §10.2). A generic Tasks write must never
    // create a FOLLOW_UP definition or retype one away — the care context
    // cannot be evaded by changing dueType (spec §8.4).
    if(isFollowUpDueType(details?.dueType) || isFollowUpDueType(originalValues?.dueType)){
        Toast.show({type: 'error', text2: `Follow Up tasks are created and managed from a person's Follow Up page.`});
        return Promise.reject(new Error('FOLLOW_UP tasks are managed via Follow Up RPCs'));
    }
    if(details.id){
        return updateTask({details, originalValues})
    }
    else{
        return createTask({details, currentUser})
    }
}


const syncAssignmentMembers = async (taskID, members, orgID) => {
    const {data: existing} = await supabase.from('TaskAssignmentMembers').select('id, personID').eq('taskID', taskID)
    const existingIDs = existing?.map(m => m.personID) || []
    const newMemberIDs = members?.map(m => m.id || m.personID).filter(Boolean) || []

    const toDelete = (existing || []).filter(m => !newMemberIDs.includes(m.personID))
    const toAdd = newMemberIDs.filter(id => !existingIDs.includes(id))

    if(toDelete.length > 0){
        await supabase.from('TaskAssignmentMembers').delete().in('id', toDelete.map(m => m.id))
    }
    if(toAdd.length > 0){
        const rows = toAdd.map((personID, i) => ({taskID, personID, organizationID: orgID, position: (i + 1) * 1000}))
        await supabase.from('TaskAssignmentMembers').insert(rows)
    }
}

// Adds a task to every event (and its future event instances) that already
// includes any of the given volunteer teams. Used by the "Add to future
// events on these teams" toggle on task create/edit. Delegates to the
// `propagate_task_to_future_events` RPC, which performs the idempotent
// EventTasks/TaskInstances inserts server-side in one call.
const propagateTaskToFutureEvents = async (taskID, teamIDs) => {
    if(!taskID || !teamIDs?.length) return;

    const {error} = await supabase.rpc('propagate_task_to_future_events', {p_task_id: taskID, p_team_ids: teamIDs});

    if(error){
        ErrorHandler(Error(error.message), {mutation: "propagateTaskToFutureEvents", taskID, teamIDs, error}, `Something went wrong adding the task to future events`)
    }
}

const createTask = async ({details, currentUser = null}) => {
    const orgID = await getCurrentOrgID();
   
    const newTask = {
        title: details.title?.trim(),
        description: details.description?.trim(),
        dueType: details.dueType,
        assumeCompleted: details.assumeCompleted,
        allowSelfAssignment: details.allowSelfAssignment,
        allowForwarding: details.allowForwarding,
        priority: details.priority ? details.priority.toUpperCase() : "STANDARD",
        organizationID: orgID,
    }

    const {error, data} = await supabase.from('Tasks').insert(newTask).select('*').single();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "createTask", input: newTask}, `Something went wrong creating that task`)
        throw Error()
    }

    if(isAutomationDueType(details.dueType)){
        Toast.delayShow({type: 'success',text2: `New task has been added successfully!`})
    }
    else if(details.dueType == 'INDIVIDUAL'){
        const {error: taskInstanceError, data: taskInstance} = await supabase.from('TaskInstances').insert({taskID: data.id, organizationID: orgID, completed: false, dueDate: details.TaskInstance.dueDate}).select('*').single();

        if(taskInstanceError){
            await supabase.from('Tasks').delete().eq('id', data.id);
            ErrorHandler(Error(taskInstanceError.message), {mutation: "createTaskInstance", input: {taskID: data.id, organizationID: orgID, completed: false, dueDate: details.TaskInstance.dueDate}}, `Something went wrong creating that task`)
            throw Error()
        }

        updateAssignees({
            taskInstanceID:taskInstance.id, 
            existingAssignees: [], 
            newAssignees: details.TaskInstance.Assignees, 
            taskInstance: { ...taskInstance, Task: data },
            orgID: orgID,
            currentUser: currentUser
        })
    }
    else{
        if(details.VolunteerTeams?.length > 0){
            const rows = details.VolunteerTeams.map(t => ({taskID: data.id, volunteerTeamID: t.id, organizationID: orgID}));
            const {error: vttError} = await supabase.from('VolunteerTeamTasks').insert(rows);
            if(vttError){
                ErrorHandler(Error(vttError.message), {mutation: "createVolunteerTeamTasks", input: {taskID: data.id, volunteerTeamIDs: details.VolunteerTeams.map(t => t.id)}, error: vttError}, `Something went wrong linking the task to volunteer teams`)
            }

            if(details.addToFutureEvents){
                await propagateTaskToFutureEvents(data.id, details.VolunteerTeams.map(t => t.id));
            }
        }

        Toast.delayShow({type: 'success',text2: `New task has been added successfully!`});

    }

    insertActivity({ subjectType: 'Task', subjectID: data.id, subjectName: data.title, verb: 'create' }).catch(() => {});

    return data;
}



const updateTask = async ({details, originalValues}) => {
    const orgID = await getCurrentOrgID();
   
    const newTask = {
        title: details.title,
        description: details.description,
        dueType: details.dueType,
        assumeCompleted: details.assumeCompleted,
        allowSelfAssignment: details.allowSelfAssignment,
        allowForwarding: details.allowForwarding,
        priority: details.priority.toUpperCase(),
        organizationID: orgID,
    }

    const {error, data} = await supabase.from('Tasks').update(newTask).eq('id', details.id).select('*, EventTasks(*, Event:Events(*)), TaskInstances(*, EventInstance:EventInstances(*)), VolunteerTeamTasks(*)').single();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateTask", input: newTask}, `Something went wrong updating that task`)
        throw Error()
    }

    data.Events = data.EventTasks?.map(et => et.Event).filter(Boolean) || [];

    if(details.VolunteerTeams !== undefined){
        const existingVtt = data.VolunteerTeamTasks || [];
        const existingTeamIds = existingVtt.map(vtt => vtt.volunteerTeamID);
        const newTeamIds = details.VolunteerTeams?.map(t => t.id) || [];

        const vttToDelete = existingVtt.filter(vtt => !newTeamIds.includes(vtt.volunteerTeamID));
        const teamIdsToAdd = newTeamIds.filter(id => !existingTeamIds.includes(id));

        if(vttToDelete.length > 0){
            await supabase.from('VolunteerTeamTasks').delete().in('id', vttToDelete.map(vtt => vtt.id));
        }

        if(teamIdsToAdd.length > 0){
            const rows = teamIdsToAdd.map(teamID => ({taskID: data.id, volunteerTeamID: teamID, organizationID: orgID}));
            const {error: vttError} = await supabase.from('VolunteerTeamTasks').insert(rows);
            if(vttError){
                ErrorHandler(Error(vttError.message), {mutation: "createVolunteerTeamTasks", input: {taskID: data.id, volunteerTeamIDs: teamIdsToAdd}, error: vttError}, `Something went wrong linking the task to volunteer teams`)
            }
        }
    }

    if(details.addToFutureEvents && details.VolunteerTeams?.length > 0){
        await propagateTaskToFutureEvents(data.id, details.VolunteerTeams.map(t => t.id));
    }

    if(details.dueType == 'INDIVIDUAL' && details.TaskInstance?.id){
        const {error: taskInstanceError} = await supabase.from('TaskInstances').update({dueDate: details.TaskInstance.dueDate}).eq('id', details.TaskInstance.id).select('*');
        
        if(taskInstanceError){
            ErrorHandler(Error(taskInstanceError.message), {mutation: "updateTaskInstance", dueDate: details.TaskInstance.dueDate}, `Something went wrong updating that task`)
            throw Error()
        }
    }
    else{
        const { itemsToAdd: eventIdsToAdd, itemsToDelete: eventIDsToDelete } = findAssociativeChanges(details.Events, data.Events);

        //Add new events to the task
        if(eventIdsToAdd && eventIdsToAdd.length > 0){
            createNewEventTasks(eventIdsToAdd, data.id)
        }

        //Remove the events from the task
        await supabase.from('EventTasks').delete().in('id', eventIDsToDelete?.map(a => a.id))

        eventIDsToDelete.forEach(eventID => {
            data.TaskInstances?.forEach(async taskInstance => {
                if(taskInstance.EventInstance?.eventID == eventID && taskInstance.EventInstance?.startTime > new Date().toISOString()){
                    //Remove future task instances on event instances of this event id
                    await supabase.from('TaskInstances').delete().eq('id', taskInstance.id);
                }
            })
        })
    }

    Toast.show({ type: 'success', text2: `Task has been updated successfully!`});

    const scalarDetails = originalValues ? buildUpdateDetails(originalValues, newTask, {
        title: { label: 'Title' },
        description: { label: 'Description' },
        priority: { label: 'Priority' },
        dueType: { label: 'Due Type' },
    }) : null;
    insertActivity({ subjectType: 'Task', subjectID: data.id, subjectName: data.title, verb: 'update', details: scalarDetails }).catch(() => {});

    return data;
}


const createNewEventTasks = async (eventIDs = [], taskID) => {
    const orgID = await getCurrentOrgID(); 

    if(!eventIDs){
        return [];
    }

    const {data: volunteerTeamTasks} = await supabase
        .from('VolunteerTeamTasks')
        .select('volunteerTeamID')
        .eq('taskID', taskID)
        .eq('organizationID', orgID);

    const newEventTasks = (volunteerTeamTasks?.length > 0 ? volunteerTeamTasks : [null]).flatMap(vt =>
        eventIDs?.map(event => ({
            organizationID: orgID,
            taskID: taskID,
            eventID: event.id,
            ...(vt ? { volunteerTeamID: vt.volunteerTeamID } : {})
        }))
    );

    const {error} = await supabase.from('EventTasks').insert(newEventTasks).select('*');

    if(error){
        ErrorHandler(Error(error.message), {mutation: "createEventTasks", eventIDs}, `Something went wrong adding the Events to the this task`)
        return [];
    }

    const now = new Date();
    const {error: getEventInstnacesError, data: selectedEventsInstances} = await supabase.from('EventInstances').select('*').eq('organizationID', orgID).in('eventID', eventIDs?.map(e => e.id)).gt('startTime', now.toISOString()).select('*');

    if(getEventInstnacesError){
        ErrorHandler(Error(getEventInstnacesError.message), {mutation: "getEventInstances", eventIDs}, `Something went wrong getting the Event Instances for the new task`)
        return [];
    }

    const newTaskInstances = (volunteerTeamTasks?.length > 0 ? volunteerTeamTasks : [null]).flatMap(vt =>
        selectedEventsInstances?.map(eventInstance => ({
            taskID: taskID,
            organizationID: orgID,
            completed: false,
            eventInstanceID: eventInstance.id,
            dueDate: eventInstance.endTime || eventInstance.startTime,
            ...(vt ? { volunteerTeamID: vt.volunteerTeamID } : {})
        }))
    );

    const {error: createTaskInstanceError} = await supabase.from('TaskInstances').insert(newTaskInstances).select('*');

    if(createTaskInstanceError){
        ErrorHandler(Error(createTaskInstanceError.message), {mutation: "createTaskInstance", selectedEventsInstances}, `Something went wrong creating the Task Instances for the new task`)
        return [];
    }

}

const createTaskInstance = async ({taskIDs, eventInstanceID, dueDate}) => {
    const orgID = await getCurrentOrgID();
    const instances = taskIDs.map((t) => {
        const taskID = typeof t === 'string' ? t : t.id;
        const volunteerTeamID = typeof t === 'string' ? null : t.volunteerTeamID;
        return {
            organizationID: orgID,
            taskID,
            eventInstanceID,
            completed: false,
            dueDate,
            ...(volunteerTeamID ? { volunteerTeamID } : {}),
        };
    });

    // Filter out instances that already exist (non-deleted) to avoid
    // TaskInstances_unique_task_eventInstance constraint violation.
    const { data: existing } = await supabase
        .from('TaskInstances')
        .select('taskID, volunteerTeamID')
        .eq('eventInstanceID', eventInstanceID)
        .eq('isDeleted', false)
        .in('taskID', instances.map(i => i.taskID));

    const existingKeys = new Set((existing || []).map(e => `${e.taskID}|${e.volunteerTeamID ?? ''}`));
    const toInsert = instances.filter(i => !existingKeys.has(`${i.taskID}|${i.volunteerTeamID ?? ''}`));

    if (toInsert.length === 0) {
        return [];
    }

    const {error, data} = await supabase.from('TaskInstances').insert(toInsert).select('*');

    if(error){
        ErrorHandler(Error(error.message), {mutation: "createTaskInstance", eventInstanceID, error}, `Something went wrong creating the Task Instance`)
        return [];
    }

    return data
}

const deleteTaskInstance = async (taskInstanceID) => {
    const {error} = await supabase.from('TaskInstances').delete().eq('id', taskInstanceID);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deleteTaskInstance", taskInstanceID}, `Something went wrong deleting the Task Instance`)
        return [];
    }
}

// Team members = People attached to the team's linked Role via RolePeople.
// Returns deduped person objects (with canManage) sorted by name.
const mapTeamMembers = (team) => {
    const seen = new Set();
    return (team?.Role?.RolePeople || [])
        .map(rp => rp?.Person ? {...rp.Person, canManage: rp.canManage} : null)
        .filter(p => {
            if(!p?.id || seen.has(p.id)) return false;
            seen.add(p.id);
            return true;
        })
        .sort((a, b) => `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(`${b.firstName || ''} ${b.lastName || ''}`));
}

const getVolunteerTeams = async () => {
    const orgID = await getCurrentOrgID();
    const { data, error } = await supabase.from('VolunteerTeams').select('*, VolunteerTeamTasks(*, Task:Tasks(id, title, description)), VolunteerTeamAssigners(*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)), Location:Locations(id, name), Role:Roles(id, name, isAccessControl, RolePeople(personID, canManage, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)))').eq('organizationID', orgID).order('name', {ascending: true});

    if(error){
        if(!isTransientError(error)){
            ErrorHandler(Error(error.message), {organizationID: orgID, error}, `Something went wrong getting volunteer teams`)
        }
        return [];
    }

    return data.map(team => ({
        ...team,
        Tasks: (team?.VolunteerTeamTasks || [])
            .filter(vtt => vtt.Task)
            .sort((a, b) => (a.position ?? 1000) - (b.position ?? 1000))
            .map(vtt => ({...vtt.Task, assigneesNeeded: vtt.assigneesNeeded || 1, position: vtt.position})),
        Assigners: team?.VolunteerTeamAssigners?.map(vta => vta.Person).filter(Boolean) || [],
        Members: mapTeamMembers(team),
    }));
}

const getVolunteerTeam = async (id) => {
    const orgID = await getCurrentOrgID();
    const { data, error } = await supabase.from('VolunteerTeams').select('*, VolunteerTeamTasks(*, Task:Tasks(id, title, description)), VolunteerTeamAssigners(*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)), Location:Locations(id, name), Role:Roles(id, name, isAccessControl, RolePeople(personID, canManage, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)))').eq('organizationID', orgID).eq('id', id).maybeSingle();

    if(error){
        if(!isTransientError(error)){
            ErrorHandler(Error(error.message), {organizationID: orgID, error}, `Something went wrong getting volunteer team`)
        }
        return null;
    }

    // Fetch events that already include this team (via EventTasks)
    const { data: eventTasks } = await supabase
        .from('EventTasks')
        .select('eventID')
        .eq('volunteerTeamID', id)
        .eq('organizationID', orgID);

    const eventIDs = [...new Set(eventTasks?.map(et => et.eventID).filter(Boolean) || [])];
    let teamEvents = [];
    let originalEventIDs = [];

    if(eventIDs.length > 0){
        const { data: events } = await supabase
            .from('Events')
            .select('*, Calendar:Calendars!inner(name, color, calendarType)')
            .eq('organizationID', orgID)
            .eq('isDeleted', false)
            .in('id', eventIDs)
            .order('name');
        teamEvents = events || [];
        originalEventIDs = eventIDs;
    }

    return {
        ...data,
        Tasks: (data?.VolunteerTeamTasks || [])
            .filter(vtt => vtt.Task)
            .sort((a, b) => (a.position ?? 1000) - (b.position ?? 1000))
            .map(vtt => ({...vtt.Task, assigneesNeeded: vtt.assigneesNeeded || 1, position: vtt.position})),
        Assigners: data?.VolunteerTeamAssigners?.map(vta => vta.Person).filter(Boolean) || [],
        Members: mapTeamMembers(data),
        Events: teamEvents,
        originalEventIDs,
    };
}


/**
 * Sync a volunteer team's tasks to selected events.
 * On new team: adds the team's tasks to each selected event + future instances.
 * On edit: adds to newly selected events, removes from deselected events + future instances.
 * Mirrors the logic from events/create.jsx Volunteer Teams dropdown + createNewEventTasks,
 * looped per selected event, scoped to the single team being edited.
 */
const syncVolunteerTeamToEvents = async (teamID, teamTasks, selectedEvents, originalEventIDs, orgID) => {
    const now = new Date().toISOString();
    const selectedEventIDs = selectedEvents?.map(e => e.id).filter(Boolean) || [];
    const originalIDs = originalEventIDs || [];

    const eventsToAdd = selectedEventIDs.filter(id => !originalIDs.includes(id));
    const eventsToRemove = originalIDs.filter(id => !selectedEventIDs.includes(id));

    // Add team's tasks to newly selected events + future instances
    if(eventsToAdd.length > 0 && teamTasks?.length > 0){
        const eventTaskRows = [];
        for(const eventID of eventsToAdd){
            for(const task of teamTasks){
                eventTaskRows.push({
                    organizationID: orgID,
                    taskID: task.id,
                    eventID,
                    volunteerTeamID: teamID,
                    position: task.position ?? 1000,
                });
            }
        }
        await supabase.from('EventTasks').insert(eventTaskRows, {ignoreDuplicates: true});

        const {data: futureInstances} = await supabase
            .from('EventInstances')
            .select('id, eventID, startTime, endTime')
            .in('eventID', eventsToAdd)
            .eq('organizationID', orgID)
            .eq('canceled', false)
            .eq('isDeleted', false)
            .gte('startTime', now);

        if(futureInstances?.length > 0){
            const taskInstanceRows = [];
            for(const ei of futureInstances){
                for(const task of teamTasks){
                    taskInstanceRows.push({
                        taskID: task.id,
                        organizationID: orgID,
                        completed: false,
                        eventInstanceID: ei.id,
                        dueDate: ei.endTime || ei.startTime,
                        volunteerTeamID: teamID,
                    });
                }
            }
            await supabase.from('TaskInstances').insert(taskInstanceRows, {ignoreDuplicates: true});
        }
    }

    // Remove team's tasks from deselected events + future instances
    if(eventsToRemove.length > 0){
        await supabase.from('EventTasks')
            .delete()
            .eq('volunteerTeamID', teamID)
            .in('eventID', eventsToRemove);

        const {data: futureInstances} = await supabase
            .from('EventInstances')
            .select('id')
            .in('eventID', eventsToRemove)
            .eq('organizationID', orgID)
            .eq('canceled', false)
            .eq('isDeleted', false)
            .gte('startTime', now);

        const futureInstanceIDs = futureInstances?.map(ei => ei.id) || [];

        if(futureInstanceIDs.length > 0){
            await supabase.from('TaskInstances')
                .delete()
                .eq('volunteerTeamID', teamID)
                .in('eventInstanceID', futureInstanceIDs);
        }
    }
};

const saveVolunteerTeam = async ({id, name, description, locationID, Tasks, Assigners, propagateToFutureEvents, originalValues, Events, originalEventIDs, roleID, Role}) => {
    const orgID = await getCurrentOrgID();

    const team = {
        id: id,
        name: name,
        description: description,
        locationID: locationID,
        roleID: Role !== undefined ? (Role?.id ?? null) : (roleID ?? null),
        organizationID: orgID
    }

    if(!team.id){
        const created = await createVolunteerTeam(team, Tasks, Assigners);
        if(created && Events?.length > 0){
            await syncVolunteerTeamToEvents(created.id, Tasks, Events, [], orgID);
        }
        return created;
    }
    else{
        const updated = await updateVolunteerTeam(team, Tasks, Assigners, propagateToFutureEvents, originalValues);
        if(updated && Events !== undefined){
            await syncVolunteerTeamToEvents(team.id, Tasks, Events, originalEventIDs || [], orgID);
        }
        return updated;
    }
}

const createVolunteerTeam = async (team, teamTasks, teamAssigners) => {
    const {error, data} = await supabase.from('VolunteerTeams').insert(team).select("*").maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "createVolunteerTeam", team}, `Something went wrong creating the Volunteer Team`)
        return null;
    }

    insertActivity({ subjectType: 'VolunteerTeam', subjectID: data.id, subjectName: data.name, verb: 'create' }).catch(() => {});

    if(data && teamTasks && teamTasks.length > 0){
        const rows = teamTasks.map((t, i) => ({organizationID: team.organizationID, volunteerTeamID: data.id, taskID: t.id, assigneesNeeded: t.assigneesNeeded || 1, position: i}));
        const {error: vttError} = await supabase.from('VolunteerTeamTasks').insert(rows);
        if(vttError){
            ErrorHandler(Error(vttError.message), {mutation: "createVolunteerTeam", teamTasks}, `The team was created, but something went wrong linking the tasks`)
        }
    }

    if(data && teamAssigners && teamAssigners.length > 0){
        const rows = teamAssigners.map(a => ({organizationID: team.organizationID, volunteerTeamID: data.id, personID: a.id}));
        const {error: vtaError} = await supabase.from('VolunteerTeamAssigners').insert(rows);
        if(vtaError){
            ErrorHandler(Error(vtaError.message), {mutation: "createVolunteerTeam", teamAssigners}, `The team was created, but something went wrong linking the assigners`)
        }
    }

    return data
}


const updateVolunteerTeam = async (team, teamTasks, teamAssigners, propagateToFutureEvents = false, originalValues = null) => {
    const {error, data} = await supabase.from('VolunteerTeams').update({name: team.name, description: team.description, locationID: team.locationID, roleID: team.roleID}).eq('id', team.id).select("*, VolunteerTeamTasks(*, Task:Tasks(id, title)), VolunteerTeamAssigners(*, Person:People(id, firstName, lastName))").maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateVolunteerTeam", team}, `Something went wrong updating the Volunteer Team`)
        return null;
    }

    // Sync tasks (upsert handles add + update of assigneesNeeded/position)
    const existingTaskIDs = data?.VolunteerTeamTasks?.map(vtt => vtt.taskID) || [];
    const newTaskIDs = teamTasks?.map(t => t.id) || [];

    const tasksToRemove = existingTaskIDs.filter(id => !newTaskIDs.includes(id));

    if(teamTasks && teamTasks.length > 0){
        const rows = teamTasks.map((t, i) => ({organizationID: team.organizationID, volunteerTeamID: team.id, taskID: t.id, assigneesNeeded: t.assigneesNeeded || 1, position: i}));
        const {error: vttError} = await supabase.from('VolunteerTeamTasks').upsert(rows, {onConflict: 'volunteerTeamID,taskID'});
        if(vttError){
            ErrorHandler(Error(vttError.message), {mutation: "updateVolunteerTeam", teamTasks}, `Something went wrong linking tasks to the team`)
        }
    }

    if(tasksToRemove.length > 0){
        const {error: vttError} = await supabase.from('VolunteerTeamTasks').delete().eq('volunteerTeamID', team.id).in('taskID', tasksToRemove);
        if(vttError){
            ErrorHandler(Error(vttError.message), {mutation: "updateVolunteerTeam", teamTasks}, `Something went wrong unlinking tasks from the team`)
        }
    }

    // Sync assigners
    const existingAssignerIDs = data?.VolunteerTeamAssigners?.map(vta => vta.personID) || [];
    const newAssignerIDs = [...new Set(teamAssigners?.map(a => a.id) || [])];

    const assignersToAdd = newAssignerIDs.filter(id => !existingAssignerIDs.includes(id));
    const assignersToRemove = existingAssignerIDs.filter(id => !newAssignerIDs.includes(id));

    if(assignersToAdd.length > 0){
        const rows = assignersToAdd.map(personID => ({organizationID: team.organizationID, volunteerTeamID: team.id, personID}));
        const {error: vtaError} = await supabase.from('VolunteerTeamAssigners').insert(rows);
        if(vtaError){
            ErrorHandler(Error(vtaError.message), {mutation: "updateVolunteerTeam", teamAssigners}, `Something went wrong linking new assigners to the team`)
        }
    }

    if(assignersToRemove.length > 0){
        const {error: vtaError} = await supabase.from('VolunteerTeamAssigners').delete().eq('volunteerTeamID', team.id).in('personID', assignersToRemove);
        if(vtaError){
            ErrorHandler(Error(vtaError.message), {mutation: "updateVolunteerTeam", teamAssigners}, `Something went wrong unlinking assigners from the team`)
        }
    }

    // Propagate task changes to future events
    if(propagateToFutureEvents && (tasksToRemove.length > 0 || teamTasks?.some(t => !existingTaskIDs.includes(t.id)))){
        const now = new Date().toISOString();

        // Find events that already include this team
        const {data: eventTeams} = await supabase
            .from('EventTasks')
            .select('eventID')
            .eq('volunteerTeamID', team.id)
            .eq('organizationID', team.organizationID);

        const eventIDs = eventTeams?.map(et => et.eventID) || [];

        if(eventIDs.length > 0){
            const {data: eventInstances} = await supabase
                .from('EventInstances')
                .select('id, eventID, startTime, endTime')
                .in('eventID', eventIDs)
                .eq('organizationID', team.organizationID)
                .eq('canceled', false)
                .eq('isDeleted', false)
                .gte('startTime', now);

            const futureInstanceIDs = eventInstances?.map(ei => ei.id) || [];

            // Add new tasks to future events
            const tasksToAdd = teamTasks?.filter(t => !existingTaskIDs.includes(t.id)) || [];
            if(tasksToAdd.length > 0){
                const newEventTasks = [];
                for(const eventID of eventIDs){
                    for(const task of tasksToAdd){
                        newEventTasks.push({
                            organizationID: team.organizationID,
                            taskID: task.id,
                            eventID,
                            volunteerTeamID: team.id
                        });
                    }
                }
                if(newEventTasks.length > 0){
                    await supabase.from('EventTasks').insert(newEventTasks, {ignoreDuplicates: true});
                }

                if(futureInstanceIDs.length > 0){
                    const instances = [];
                    for(const ei of eventInstances){
                        for(const task of tasksToAdd){
                            instances.push({
                                taskID: task.id,
                                organizationID: team.organizationID,
                                completed: false,
                                eventInstanceID: ei.id,
                                dueDate: ei.endTime || ei.startTime,
                                volunteerTeamID: team.id
                            });
                        }
                    }
                    await supabase.from('TaskInstances').insert(instances, {ignoreDuplicates: true});
                }
            }

            // Remove dropped tasks from future events
            if(tasksToRemove.length > 0){
                await supabase.from('EventTasks')
                    .delete()
                    .eq('volunteerTeamID', team.id)
                    .in('taskID', tasksToRemove);

                if(futureInstanceIDs.length > 0){
                    await supabase.from('TaskInstances')
                        .delete()
                        .eq('volunteerTeamID', team.id)
                        .in('taskID', tasksToRemove)
                        .in('eventInstanceID', futureInstanceIDs);
                }
            }
        }
    }

    const scalarDetails = originalValues ? buildUpdateDetails(originalValues, team, { name: { label: 'Name' }, description: { label: 'Description' } }) : null;
    const taskDetails = [
        ...tasksToRemove.map(id => {
            const t = data.VolunteerTeamTasks?.find(vtt => vtt.taskID === id);
            return t?.Task?.title ? `- Removed task '${t.Task.title}'` : null;
        }),
        ...teamTasks.filter(t => !existingTaskIDs.includes(t.id)).map(t => `- Added task '${t.title || 'Task'}'`),
    ].filter(Boolean);
    const assignerDetails = [
        ...assignersToAdd.map(id => {
            const a = data.VolunteerTeamAssigners?.find(vta => vta.personID === id);
            return a?.Person ? `- Added assigner '${a.Person.firstName} ${a.Person.lastName}'.trim()` : null;
        }),
        ...assignersToRemove.map(id => {
            const a = data.VolunteerTeamAssigners?.find(vta => vta.personID === id);
            return a?.Person ? `- Removed assigner '${a.Person.firstName} ${a.Person.lastName}'.trim()` : null;
        }),
    ].filter(Boolean);
    insertActivity({ subjectType: 'VolunteerTeam', subjectID: team.id, subjectName: team.name, verb: 'update', details: joinDetails(scalarDetails, taskDetails, assignerDetails) }).catch(() => {});

    return data
}

const deleteVolunteerTeam = async (teamID) => {
    const {data: existing} = await supabase.from('VolunteerTeams').select('name').eq('id', teamID).maybeSingle();

    const {error} = await supabase.from('VolunteerTeams').delete().eq('id', teamID);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deleteVolunteerTeam", teamID}, `Something went wrong deleting the Volunteer Team`)
        return [];
    }

    insertActivity({ subjectType: 'VolunteerTeam', subjectID: teamID, subjectName: existing?.name, verb: 'delete' }).catch(() => {});
}

// ========== Task Assignment Templates ==========

const getTaskAssignmentTemplates = async () => {
    const orgID = await getCurrentOrgID();
    const { data, error } = await supabase
        .from('TaskAssignmentTemplates')
        .select('*, VolunteerTeam:VolunteerTeams(id, name), Members:TaskAssignmentTemplateMembers(*, Task:Tasks(id, title), Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey))')
        .eq('organizationID', orgID)
        .order('name', {ascending: true});

    if(error){
        if(!isTransientError(error)){
            ErrorHandler(Error(error.message), {organizationID: orgID, error}, `Something went wrong getting task assignment templates`)
        }
        return [];
    }

    return data;
}

const getTaskAssignmentTemplate = async (id) => {
    const { data, error } = await supabase
        .from('TaskAssignmentTemplates')
        .select('*, VolunteerTeam:VolunteerTeams(id, name), Members:TaskAssignmentTemplateMembers(*, Task:Tasks(id, title), Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey))')
        .eq('id', id)
        .maybeSingle();

    if(error){
        if(!isTransientError(error)){
            ErrorHandler(Error(error.message), {id, error}, `Something went wrong getting task assignment template`)
        }
        return null;
    }

    return data;
}

const saveTaskAssignmentTemplate = async ({id, name, volunteerTeamID, members}) => {
    const orgID = await getCurrentOrgID();

    const template = {
        name: name,
        volunteerTeamID: volunteerTeamID,
        organizationID: orgID
    }

    let templateID = id;

    if(!id){
        const {error, data} = await supabase.from('TaskAssignmentTemplates').insert(template).select("*").maybeSingle();
        if(error){
            ErrorHandler(Error(error.message), {mutation: "createTaskAssignmentTemplate", template}, `Something went wrong creating the Task Assignment Template`)
            throw Error()
        }
        templateID = data.id;
    }
    else{
        const {error} = await supabase.from('TaskAssignmentTemplates').update(template).eq('id', id);
        if(error){
            ErrorHandler(Error(error.message), {mutation: "updateTaskAssignmentTemplate", template}, `Something went wrong updating the Task Assignment Template`)
            throw Error()
        }
        // Delete existing members to rebuild
        await supabase.from('TaskAssignmentTemplateMembers').delete().eq('templateID', id);
    }

    if(members && members.length > 0){
        const rows = members.map((m, i) => ({
            templateID: templateID,
            taskID: m.taskID,
            personID: m.personID,
            position: m.position ?? i,
            organizationID: orgID
        }));

        const {error: memberError} = await supabase.from('TaskAssignmentTemplateMembers').insert(rows);
        if(memberError){
            ErrorHandler(Error(memberError.message), {mutation: "createTemplateMembers", rows}, `Something went wrong saving template members`)
            throw Error()
        }
    }

    return {id: templateID};
}

const deleteTaskAssignmentTemplate = async (templateID) => {
    const {error} = await supabase.from('TaskAssignmentTemplates').delete().eq('id', templateID);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deleteTaskAssignmentTemplate", templateID}, `Something went wrong deleting the Task Assignment Template`)
        return [];
    }
}

const applyAssignmentTemplate = async ({templateID, eventInstanceID, currentUser = null}) => {
    const {error, data} = await supabase.rpc('apply_assignment_template', {
        p_template_id: templateID,
        p_event_instance_id: eventInstanceID
    });

    if(error){
        ErrorHandler(Error(error.message), {mutation: "applyAssignmentTemplate", templateID, eventInstanceID}, `Something went wrong applying the assignment template`)
        throw Error()
    }

    // Send TASK_ASSIGNED notifications for primary assignees (position < assigneesNeeded),
    // matching the individual-assign logic in updateAssignees. Skip self-assignments.
    if(data?.length > 0){
        const notificationsToSend = [];

        data.forEach(row => {
            if(row.personID === currentUser) return;
            if(row.position >= row.assigneesNeeded) return;

            const eventDate = new Date(row.eventStartTime);
            const teamSuffix = row.teamName ? ` (${row.teamName})` : '';
            const message = `${row.taskTitle}${teamSuffix} has been assigned to you for ${row.eventName} on ${eventDate.toLocaleString("en-US", {month:"2-digit", day:"2-digit", year:"numeric", hour:"numeric", minute:"2-digit"})}`;

            notificationsToSend.push({
                title: "You've been assigned a new task",
                path: `tasks/instances/${row.taskInstanceID}`,
                type: "TASK_ASSIGNED",
                targetPersonID: row.personID,
                message: message,
                parameters: {taskInstanceID: row.taskInstanceID},
            });
        });

        if(notificationsToSend.length > 0){
            createNotification(notificationsToSend);
        }
    }

    return data;
}

// People assigned to a given task (by taskID) within the last 6 months.
// Returns deduped Person rows (most-recently-assigned first) in the same
// shape used by the people SheetSelector so they can be pinned on top.
const getRecentAssigneesForTask = async (taskID) => {
    const orgID = await getCurrentOrgID();
    const sixMonthsAgo = new Date(Date.now() - 1000 * 60 * 60 * 24 * 182).toISOString();

    const { data, error } = await supabase
        .from('TaskInstancePeople')
        .select(`
            createdAt,
            personID,
            TaskInstance:TaskInstances!inner(
                id, taskID, dueDate, isDeleted
            )
        `)
        .eq('organizationID', orgID)
        .eq('TaskInstance.taskID', taskID)
        .eq('TaskInstance.isDeleted', false)
        .gte('TaskInstance.dueDate', sixMonthsAgo)
        .order('createdAt', { ascending: false });

    if (error) {
        if(!isTransientError(error)){
            ErrorHandler(Error(error.message), {taskID, error}, `Something went wrong getting recent assignees`);
        }
        return [];
    }

    const seen = new Set();
    const personIds = [];
    (data || []).forEach(row => {
        if (!row.personID || seen.has(row.personID)) return;
        seen.add(row.personID);
        personIds.push(row.personID);
    });

    if (personIds.length === 0) return [];

    const { data: people, error: peopleError } = await supabase.rpc('filter_people', {
        filters: { conditions: [{ fieldId: 'id', operator: 'in', value: personIds }] },
        organizationid: orgID,
        logictype: 'AND',
        record_limit: personIds.length,
        record_offset: 0,
    });

    if (peopleError) {
        if(!isTransientError(peopleError)){
            ErrorHandler(Error(peopleError.message), {taskID, personIds, error: peopleError}, `Something went wrong getting recent assignee people`);
        }
        return [];
    }

    const results = people?.[0]?.people?.results || [];
    const byId = new Map(results.map(p => [p.id, p]));
    return personIds.map(id => byId.get(id)).filter(Boolean);
}

// Batch version: returns a map of taskID → deduped Person rows (most-recent first)
// for all given taskIDs, assigned within the last 6 months.
const getRecentAssigneesForTasks = async (taskIDs) => {
    if (!taskIDs?.length) return {};
    const orgID = await getCurrentOrgID();
    const sixMonthsAgo = new Date(Date.now() - 1000 * 60 * 60 * 24 * 182).toISOString();

    const { data, error } = await supabase
        .from('TaskInstancePeople')
        .select(`
            createdAt,
            personID,
            TaskInstance:TaskInstances!inner(
                id, taskID, dueDate, isDeleted
            )
        `)
        .eq('organizationID', orgID)
        .in('TaskInstance.taskID', taskIDs)
        .eq('TaskInstance.isDeleted', false)
        .gte('TaskInstance.dueDate', sixMonthsAgo)
        .order('createdAt', { ascending: false });

    if (error) {
        if(!isTransientError(error)){
            ErrorHandler(Error(error.message), {taskIDs, error}, `Something went wrong getting recent assignees batch`);
        }
        return {};
    }

    const seen = new Set();
    const taskToPersonIds = {};
    (data || []).forEach(row => {
        const taskID = row.TaskInstance?.taskID;
        if (!taskID || !row.personID) return;
        if (!taskToPersonIds[taskID]) taskToPersonIds[taskID] = { ids: [], seen: new Set() };
        if (taskToPersonIds[taskID].seen.has(row.personID)) return;
        taskToPersonIds[taskID].seen.add(row.personID);
        taskToPersonIds[taskID].ids.push(row.personID);
        if (!seen.has(row.personID)) {
            seen.add(row.personID);
        }
    });

    const personIds = Array.from(seen);
    if (personIds.length === 0) return {};

    const { data: people, error: peopleError } = await supabase.rpc('filter_people', {
        filters: { conditions: [{ fieldId: 'id', operator: 'in', value: personIds }] },
        organizationid: orgID,
        logictype: 'AND',
        record_limit: personIds.length,
        record_offset: 0,
    });

    if (peopleError) {
        if(!isTransientError(peopleError)){
            ErrorHandler(Error(peopleError.message), {taskIDs, personIds, error: peopleError}, `Something went wrong getting recent assignee people batch`);
        }
        return {};
    }

    const results = people?.[0]?.people?.results || [];
    const byId = new Map(results.map(p => [p.id, p]));
    const map = {};
    Object.keys(taskToPersonIds).forEach(taskID => {
        map[taskID] = taskToPersonIds[taskID].ids
            .map(id => byId.get(id))
            .filter(Boolean);
    });
    return map;
}

export {
    getTasks,
    getTask,
    deleteTask,
    upcomingTaskInstancesForPerson,
    upcomingTaskInstancesAsAssigner,
    upcomingTaskInstancesForTeam,
    getPersonTaskHistory,
    saveTask,
    updateAssignees,
    updateAssigneeResponse,
    updateTaskCompletionStatus,
    getTaskInstance,
    getTaskInstancesByTask,
    getTaskInstancesByEventInstance,
    getIndividualTaskInstances,
    createTaskInstance,
    deleteTaskInstance,
    getAssigneesNeeded,
    getConflictingTaskTitles,
    getVolunteerTeams,
    saveVolunteerTeam,
    deleteVolunteerTeam,
    getVolunteerTeam,
    getTaskAssignmentTemplates,
    getTaskAssignmentTemplate,
    saveTaskAssignmentTemplate,
    deleteTaskAssignmentTemplate,
    applyAssignmentTemplate,
    getRecentAssigneesForTask,
    getRecentAssigneesForTasks,
    syncAssignmentMembers,
}